/* global FB */
import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import { useForm } from 'react-hook-form'
import Helmet from 'react-helmet'
import DOMPurify from 'dompurify'
import dayjs from '../../utils/dayjs'
import classnames from 'classnames'
import _ from 'lodash'
import { contestStateSelector as stateSelector } from 'reduxModules/contests/selectors'
import { contestDispatchers } from 'reduxModules/contests/dispatchers'
import { contestsStatus } from 'reduxModules/contests/'
import withRouterV6 from '../../utils/withRouterV6'
import keyUpHandler from '../utils/keyUpHandler'
import '../generic/RenderedField/styles.css'
import './styles.css'

const EMAIL_PATTERN = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i

export function Contest({
  loadContest,
  onSubmit,
  status,
  bannerImage,
  isOver,
  startDate,
  title,
  facebookShareImages,
  bullets,
  isMobile,
  newsletter,
  toggleNewsletter
}) {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm()

  useEffect(() => {
    loadContest()
  }, [loadContest])

  if (status !== contestsStatus.success) return null

  const hasBanner = !_.isNull(bannerImage.normal)
  const hasStarted = dayjs().isAfter(dayjs(startDate))
  const formattedStartDate = dayjs(startDate, 'YYYY-MM-DD HH:mm:ss').toString()

  return (
    <div id="Contest">
      <div
        className="background-image"
        style={{ backgroundImage: hasBanner ? `url(${bannerImage.normal})` : 'none' }}
      />
      <Helmet>
        <title>{`${title} | Stan Winston School of Character Arts`}</title>
        <meta property="og:title" content={title} />
        {facebookShareImages.length > 0 && facebookShareImages.map((fbImage, fbImageIndex) => (
          <meta property="og:image" content={fbImage.original} key={fbImageIndex} />
        ))}
        <meta content="article" property="og:type" />
      </Helmet>
      <section id="contest-box">
        <section id="content" className="wrapper">
          <div className="title">
            <h1>{title}</h1>
          </div>
          {bullets && (
            <div
              className={classnames('body', { wrapper: isMobile })}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(bullets) }}
            />
          )}
        </section>
        {!isOver && hasStarted && (
          <section id="participate" className="wrapper participate-form">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className={classnames('field', '-floating', { '-error': errors.fullName })}>
                <div className="field-container">
                  <input
                    {...register('fullName', {
                      required: 'This field is required.'
                    })}
                    type="text"
                    placeholder=" "
                    id="contest-full-name"
                  />
                  <label htmlFor="contest-full-name">Full Name</label>
                  <span
                    className={`field__error${!errors.fullName ? ' field__error--hidden' : ''}`}
                    role="alert"
                  >
                    {errors.fullName?.message}
                  </span>
                </div>
              </div>
              <div className={classnames('field', '-floating', { '-error': errors.email })}>
                <div className="field-container">
                  <input
                    {...register('email', {
                      required: 'This field is required.',
                      pattern: {
                        value: EMAIL_PATTERN,
                        message: 'Invalid email address.'
                      }
                    })}
                    type="text"
                    placeholder=" "
                    id="contest-email"
                  />
                  <label htmlFor="contest-email">Email</label>
                  <span
                    className={`field__error${!errors.email ? ' field__error--hidden' : ''}`}
                    role="alert"
                  >
                    {errors.email?.message}
                  </span>
                </div>
              </div>
              <div className="newsletter">
                <div className="checkbox-area">
                  <div className="checkbox-text">
                    <div>
                      Let me know about upcoming contests, behind-the-scenes features, new course content &amp; exclusive discounts!
                    </div>
                  </div>
                  <div className="checkbox-confirm">
                    <input
                      type="checkbox"
                      name="newsletter"
                      checked={newsletter}
                      value={newsletter}
                      id="newsletter-checkbox"
                      onChange={() => toggleNewsletter()}
                      onKeyUp={event => keyUpHandler(event, toggleNewsletter)}
                    />
                    <label htmlFor="newsletter-checkbox"> Yes, let me know </label>
                  </div>
                </div>
              </div>
              <div className="buttons">
                <button type="submit" className="btn -orange">Enter contest</button>
              </div>
            </form>
          </section>
        )}
        <section className="message">
          <div className="message-body">
            {!hasStarted && !isOver && <span>This contest starts on {formattedStartDate}</span>}
            {isOver && <span>This contest is over</span>}
          </div>
        </section>
      </section>
    </div>
  )
}

Contest.propTypes = {
  loadContest: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  status: PropTypes.string,
  bannerImage: PropTypes.shape({
    normal: PropTypes.string
  }),
  isOver: PropTypes.bool,
  startDate: PropTypes.string,
  title: PropTypes.string,
  facebookShareImages: PropTypes.array,
  bullets: PropTypes.string,
  isMobile: PropTypes.bool,
  newsletter: PropTypes.bool,
  toggleNewsletter: PropTypes.func
}

export default withRouterV6(connect(
  stateSelector,
  contestDispatchers
)(Contest))
